import { Box, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Neutrals, Primary } from '../core-components/theme/color-palette/colors';
import Button from '../core-components/theme/components/Button/Button';
import DialogBox from '../core-components/theme/components/DialogBox/DialogBox';
import ApiBasedAutoComplete from '../globalComponents/apiBasedAutoComplete';
import { createViewAsUserToken } from '../services/newUsers';
import useUserStore from '../store/UserStore';
import { decodeToken } from '../utils/jwt.utils';
import { useUnifiedDashboardStore } from '../store/UnifiedDashboardStore';
import { useDashboardStore } from '../store/DashboardStore';
import { trackEvent } from '../modules/analytics/trackingMiddleware';
import { VIEWAS_SWITCH_USER } from '../modules/analytics/events';

interface IViewAsProps {
  open: boolean;
  handleClose: Function;
}
interface IUser {
  _id: string;
  name: string;
}
function ViewAs({ open, handleClose }: IViewAsProps) {
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState<IUser>({ _id: '', name: '' });
  const navigate = useNavigate();
  const { setToken, setSideBar, setUserId, setLandingPage } = useUserStore();
  const { resetUnifiedDashboardStore } = useUnifiedDashboardStore();
  const { resetDashboardStore } = useDashboardStore();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  // Mutation for creating a View As User token
  const mutation = useMutation({
    mutationFn: (viewAsUserId: string) => createViewAsUserToken(viewAsUserId),
    onSuccess: data => {
      toast.success(t('viewAs.toast.success', 'View As User Token created successfully'));
      handleClose();
      queryClient.clear();

      // authz-v2 TODO: impersonation still works by swapping a localStorage token.
      // Under cookie-only auth this must become a backend "view as" flow that issues
      // an impersonation httpOnly cookie (enter/exit endpoints) and returns identity
      // in the body. Until that ships, ViewAs does not function (no token to swap).
      const originalToken = localStorage.getItem('token');
      if (originalToken) {
        localStorage.setItem('originalToken', originalToken);
        setToken(originalToken);
      }
      if(!!data.token) localStorage.setItem('token', data.token);

      const originalSideBar = localStorage.getItem('sideBar');
      if (originalSideBar) {
        localStorage.setItem('originalSideBar', originalSideBar);
        setSideBar(JSON.parse(originalSideBar));
      }
      localStorage.setItem('sideBar', JSON.stringify(data?.layoutInfo?.sideBar));

      const originalUserId = localStorage.getItem('userId');
      if (originalUserId) {
        localStorage.setItem('originalUserId', originalUserId);
        setUserId(originalUserId);
      }
      localStorage.setItem('userId', selectedUser?._id!);
      setUserId(selectedUser?._id!);

      const originalLandingPage = localStorage.getItem('landingPage');
      if (originalLandingPage) {
        localStorage.setItem('originalLandingPage', originalLandingPage);
        setLandingPage(data.layoutInfo.landingPage);
      }
      localStorage.setItem('landingPage', data?.layoutInfo?.landingPage);

      localStorage.setItem('isViewing', 'true');
      
      resetUnifiedDashboardStore();
      resetDashboardStore();
      
      
      const userTokenDetails = localStorage.getItem('token');
      const originalTokenDetails = localStorage.getItem('originalToken');

      if(userTokenDetails && originalTokenDetails) {
        const decodedUserTokenDetails = decodeToken(userTokenDetails);
        const decodedOriginalTokenDetails = decodeToken(originalTokenDetails);

        const userId = decodedOriginalTokenDetails.userId;
        const email = decodedOriginalTokenDetails?.email || '';
        const userName = decodedOriginalTokenDetails?.userName || '';
        const isStaff = decodedOriginalTokenDetails?.isStaff || false;
        const viewAsUser = decodedUserTokenDetails?.viewAsUser || false;

        const viewAsUserName = decodedUserTokenDetails?.userName || '';
        const viewAsUserEmail = decodedUserTokenDetails?.email || '';

        localStorage.setItem(
          'posthogdetails',
          JSON.stringify({
            distinctId: userId,
            userProperties: {
              email: email,
              name: userName,
              internalTeam: isStaff,
              ...(viewAsUser && {
                viewAsUserName: viewAsUserName,
                viewAsUserEmail: viewAsUserEmail,
              }),
            },
          })
        );
      }
      
      navigate(`${data?.layoutInfo?.landingPage}`);
      trackEvent(VIEWAS_SWITCH_USER, { targetUserId: selectedUser?._id, targetUserName: selectedUser?.name });
    },
    onError: (err: any) => {
      toast.error(err?.message || t('viewAs.toast.error', 'Error creating View As User Token'));
      console.error('Error creating View As User Token:', err);
    },
  });

  const handleSubmit = () => {
    if (selectedUser) {
      mutation.mutate(selectedUser?._id);
    } else {
      toast.error(t('viewAs.toast.selectUser', 'Please select a user first.'));
    }
  };

  return (
    <Box>
      <DialogBox
        open={open}
        title={t('viewAs.dialog.title', 'View As')}
        handleClose={handleClose}
        padding="20px"
        // contentPadding="20px 0px 10px 0px"
      >
        <ApiBasedAutoComplete
          onSelect={(newValue: IUser | null) => {
            setSelectedUser(newValue || { _id: '', name: '' });
          }}
          apiPath={'/newUsers/getViewAsUserList'}
          searchKey="query"
          keyToPick="name"
          idKey="_id"
          label={t('viewAs.field.selectUser', 'Select User')}
          selectedOptions={selectedUser}
        />

        <Stack direction="row" mt={9} columnGap={2} justifyContent="space-between">
          <Button
            variant="outlined"
            sx={{
              border: `1px solid ${Neutrals[500]}`,
              borderRadius: '4px',
              padding: isMobile ? '10px 40px' : '10px 70px',
              flex: isMobile ? 1 : 0,
              minWidth: '47%',
            }}
            onClick={() => {
              handleClose();
            }}
          >
            <Typography variant="body" lineHeight="24px">
              {t('viewAs.button.cancel', 'CANCEL')}
            </Typography>
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            sx={{
              minWidth: '47%',
              padding: isMobile ? '10px 40px' : '10px 70px',
              flex: isMobile ? 1 : 0,
              borderRadius: '4px',
              boxShadow: 'none',
              ':hover': { boxShadow: 'none', backgroundColor: `${Primary[700]}` },
            }}
          >
            <Typography variant="body" lineHeight="24px" color="white">
              {t('viewAs.button.submit', 'SUBMIT')}
            </Typography>
          </Button>
        </Stack>
      </DialogBox>
    </Box>
  );
}

export default ViewAs;
